'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Pencil, MapPin, Plus } from 'lucide-react';
import { siteAttendanceApi, type Checkpoint } from '@/services/api/site-attendance';

export default function CheckpointsListPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const s = t.siteAttendance;
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCheckpoints = useCallback(async () => {
    try {
      const data = await siteAttendanceApi.listCheckpoints();
      setCheckpoints(data.checkpoints);
    } catch { /* */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCheckpoints(); }, [loadCheckpoints]);

  const geoPolicyLabel: Record<string, string> = {
    STRICT_BLOCK: s?.geoPolicyBlockShort ?? 'Strict Block',
    ALLOW_WITH_FLAG: s?.geoPolicyFlagShort ?? 'Allow w/ Flag',
    SKIP: s?.geoPolicySkipShort ?? 'Skip',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-[#1f2329]">{s?.checkpoints ?? 'Checkpoints'}</h1>
          <p className="text-sm text-gray-400 mt-0.5">{s?.checkpointsDesc ?? 'Manage attendance check-in locations'}</p>
        </div>
        <Link href="/siteattendance/admin/checkpoints/create">
          <Button className="gap-1.5"><Plus className="w-4 h-4" />{s?.createCheckpoint ?? 'Create'}</Button>
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : checkpoints.length === 0 ? (
          <div className="p-16 text-center">
            <MapPin className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">{s?.noCheckpoints ?? 'No checkpoints yet'}</p>
            <Link href="/siteattendance/admin/checkpoints/create">
              <Button variant="outline" size="sm" className="mt-4">{s?.createCheckpoint ?? 'Create'}</Button>
            </Link>
          </div>
        ) : (
          <Table>
            <TableHeader className="bg-[#fafafa] [&_tr]:border-b-0">
              <TableRow className="hover:bg-transparent border-0">
                <TableHead className="text-[#666]">{s?.name ?? 'Name'}</TableHead>
                <TableHead className="text-[#666]">{s?.timezone ?? 'Timezone'}</TableHead>
                <TableHead className="text-[#666]">{s?.geoPolicy ?? 'Geo Policy'}</TableHead>
                <TableHead className="text-[#666]">{s?.guestAccess ?? 'Guest'}</TableHead>
                <TableHead className="text-[#666]">{t.common?.status ?? 'Status'}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-gray-100">
              {checkpoints.map((cp) => (
                <TableRow
                  key={cp.id}
                  className="cursor-pointer hover:bg-gray-50 border-0"
                  onClick={() => { window.location.href = `/siteattendance/admin/checkpoints/${cp.id}`; }}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#1f2329] truncate">{cp.name}</div>
                        {cp.address && <div className="text-xs text-gray-400 truncate">{cp.address}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">{cp.timezone}</TableCell>
                  <TableCell>
                    <Badge variant={cp.geoPolicy === 'SKIP' ? 'secondary' : 'default'}>
                      {geoPolicyLabel[cp.geoPolicy] ?? cp.geoPolicy}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs ${cp.allowUnauthenticatedCheckin ? 'text-green-600' : 'text-gray-400'}`}>
                      {cp.allowUnauthenticatedCheckin ? (s?.enabled ?? 'On') : (s?.disabled ?? 'Off')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 text-xs ${cp.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${cp.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                      {cp.isActive ? (s?.active ?? 'Active') : (s?.inactive ?? 'Inactive')}
                    </span>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => router.push(`/siteattendance/admin/checkpoints/${cp.id}/edit`)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
